import { Controller, Loader, IDynamicLoader, UseBefore, skipWithDefault } from '@tencent/workbench';
import { Form, NumberInputFrom } from '@tencent/wb-ext-form';
import { FormItemName, IMultiDiffValue, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { FORM_CONFIGS, SCALE_DEPTH_3D_MAX } from './config';
import type { CameraPresetType, MaterialType } from '@tencent/docs-renderer-3d';
import { ISelectOption, IThreeDFeature } from './three-d-interface';
import { ThreeDDisabledTooltipMiddleware } from '@src/framework/workbench/middleware/three-d-disabled-tooltip';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { ANGLE_MAX } from '../../../common/config';

@Controller({ formItems: FORM_CONFIGS })
export class ThreeDController {
    // @ts-ignore
    public constructor(@Loader(IThreeDFeature) private loader: IDynamicLoader<IThreeDFeature>) {}

    private get feature() {
        return this.loader.instance!;
    }

    @Form(FormItemName.RESET_3D, 'content')
    public renderReset3DContent() {
        return this.feature.renderReset3d();
    }

    @Form(FormItemName.LAYOUT_ROTATE_3D_X, 'value')
    public getRotation3DX(): number | IMultiDiffValue {
        const rotate3DXObj = { rotation3DX: this.feature.rotation3D?.x ?? 0 };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.THREE_D, rotate3DXObj);
    }

    @Form(FormItemName.LAYOUT_ROTATE_3D_X, 'onChange')
    public changeRotation3DX(value: number, from: NumberInputFrom): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.ROTATION_X_3D,
            ver6: value,
        });
        const { rotation3DX } = this.feature.getValidValue({ rotation3DX: value }, from, ANGLE_MAX);
        this.feature.handleRotation3DChange({ x: rotation3DX });
    }

    @Form(FormItemName.LAYOUT_ROTATE_3D_Y, 'value')
    public getRotation3DY(): number | IMultiDiffValue {
        const rotate3DYObj = { rotation3DY: this.feature.rotation3D?.y ?? 0 };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.THREE_D, rotate3DYObj);
    }

    @Form(FormItemName.LAYOUT_ROTATE_3D_Y, 'onChange')
    public changeRotation3DY(value: number, from: NumberInputFrom): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.ROTATION_Y_3D,
            ver6: value,
        });
        const { rotation3DY } = this.feature.getValidValue({ rotation3DY: value }, from, ANGLE_MAX);
        this.feature.handleRotation3DChange({ y: rotation3DY });
    }

    @Form(FormItemName.LAYOUT_ROTATE_3D_Z, 'value')
    public getRotation3DZ(): number | IMultiDiffValue {
        const rotate3DZObj = { rotation3DZ: this.feature.rotation3D?.z ?? 0 };
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.THREE_D, rotate3DZObj);
    }

    @Form(FormItemName.LAYOUT_ROTATE_3D_Z, 'onChange')
    public changeRotation3DZ(value: number, from: NumberInputFrom): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.ROTATION_Z_3D,
            ver6: value,
        });
        const { rotation3DZ } = this.feature.getValidValue({ rotation3DZ: value }, from, ANGLE_MAX);
        this.feature.handleRotation3DChange({ z: rotation3DZ });
    }

    @Form(FormItemName.SCALE_DEPTH_3D, 'value')
    public getDepth3D(): number | IMultiDiffValue {
        const depth3D = this.feature.depth3D ?? NaN;
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.THREE_D, { depth3D });
    }

    @Form(FormItemName.SCALE_DEPTH_3D, 'onChange')
    public changeDepth3D(value: number, from: NumberInputFrom): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.DEPTH_3D,
            ver6: value,
        });
        const { depth3D } = this.feature.getValidValue({ depth3D: value }, from, SCALE_DEPTH_3D_MAX);
        this.feature.handleDepth3DChange(depth3D);
    }

    @Form(FormItemName.CHANGE_DEPTH_COLOR_3D, 'value')
    public getExtrusionColor() {
        return this.feature.depthColor ? `#${this.feature.depthColor}` : '#FFFFFF';
    }

    @Form(FormItemName.SHOW_FLAT_TEXT, 'value')
    public getShowFlatText() {
        return this.feature.isFlatText;
    }

    @Form(FormItemName.SHOW_FLAT_TEXT, 'onChange')
    public handleShowFlatTextChange(isFlatText: boolean) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.DEPTH_COLOR_3D,
            ver5: isFlatText ? 1 : 2,
        });
        this.feature.handleShowFlatTextChange(isFlatText);
    }

    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_HEIGHT, 'value')
    public getFrontFrustumHeight(): number | undefined {
        return this.feature.frontFrustumInfo?.height ?? 0;
    }

    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_HEIGHT, 'onChange')
    public setFrontFrustumHeight(value: number): void {
        this.feature.handleFrustumChange({
            front: {
                height: value,
            },
        });
    }

    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_WIDTH, 'value')
    public getFrontFrustumWidth(): number | undefined {
        return this.feature.frontFrustumInfo?.width ?? 0;
    }

    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_WIDTH, 'onChange')
    public setFrontFrustumWidth(value: number): void {
        this.feature.handleFrustumChange({
            front: {
                width: value,
            },
        });
    }

    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_HEIGHT, 'value')
    public getBackFrustumHeight(): number | undefined {
        return this.feature.backFrustumInfo?.height ?? 0;
    }

    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_HEIGHT, 'onChange')
    public setBackFrustumHeight(value: number): void {
        this.feature.handleFrustumChange({
            back: {
                height: value,
            },
        });
    }

    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_WIDTH, 'value')
    public getBackFrustumWidth(): number | undefined {
        return this.feature.backFrustumInfo?.width ?? 0;
    }

    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_WIDTH, 'onChange')
    public setBackFrustumWidth(value: number): void {
        this.feature.handleFrustumChange({
            back: {
                width: value,
            },
        });
    }

    @Form(FormItemName.CHANGE_3D_LIGHT_TYPE, 'value')
    public getChange3DLightTypeName() {
        return this.feature.lightType.key;
    }

    @Form(FormItemName.CHANGE_3D_LIGHT_TYPE, 'onChange')
    public set3DLightType(lightType: string): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.LIGHT_TYPE_3D,
            ver6: lightType,
        });
        this.feature.handleLightTypeChange(lightType);
    }

    @Form(FormItemName.CHANGE_LIGHT_ROTATION_ANGLE, 'value')
    public getLightRotationAngle(): number | IMultiDiffValue {
        const lightRotationAngle = this.feature.lightRotationAngle ?? 0;
        return this.feature.getShownValue(INPUT_PROPS_CALLBACK_KEY.THREE_D, { lightRotationAngle });
    }

    @Form(FormItemName.CHANGE_LIGHT_ROTATION_ANGLE, 'onChange')
    public setLightRotationAngle(angle: number, from: NumberInputFrom): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.LIGHT_ROTATION_3D,
            ver6: angle,
        });
        const { lightRotationAngle } = this.feature.getValidValue({ lightRotationAngle: angle }, from, ANGLE_MAX);
        this.feature.handleLightRotationAngleChange(lightRotationAngle);
    }

    @Form(FormItemName.CHANGE_MATERIAL_TYPE, 'value')
    public getMaterialType() {
        return this.feature.materialType ?? 'warmMatte';
    }

    @Form(FormItemName.CHANGE_MATERIAL_TYPE, 'onChange')
    public setMaterialType(materialType: MaterialType): void {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.MATERIAL_TYPE_3D,
            ver6: materialType,
        });
        this.feature.handleMaterialTypeChange(materialType);
    }

    @Form(FormItemName.CHANGE_CAMERA_PRESET, 'value')
    public getCameraPreset() {
        return this.feature.cameraPreset ?? '';
    }

    @Form(FormItemName.CHANGE_CAMERA_PRESET, 'onChange')
    public setCameraPreset(cameraPreset: CameraPresetType) {
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.SIDEBAR_LAYOUT_3D,
            action: TDWActionType.ROTATION_PRESET_3D,
            ver6: cameraPreset,
        });
        this.feature.handleCameraPresetChange(cameraPreset);
    }

    @Form(FormItemName.RESET_3D, 'shouldDisabled')
    @Form(FormItemName.LAYOUT_ROTATE_3D_X, 'shouldDisabled')
    @Form(FormItemName.LAYOUT_ROTATE_3D_Y, 'shouldDisabled')
    @Form(FormItemName.LAYOUT_ROTATE_3D_Z, 'shouldDisabled')
    @Form(FormItemName.CHANGE_DEPTH_COLOR_3D, 'shouldDisabled')
    @Form(FormItemName.SHOW_FLAT_TEXT, 'shouldDisabled')
    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_HEIGHT, 'shouldDisabled')
    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_WIDTH, 'shouldDisabled')
    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_HEIGHT, 'shouldDisabled')
    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_WIDTH, 'shouldDisabled')
    @Form(FormItemName.CHANGE_3D_LIGHT_TYPE, 'shouldDisabled')
    @Form(FormItemName.CHANGE_LIGHT_ROTATION_ANGLE, 'shouldDisabled')
    @Form(FormItemName.CHANGE_MATERIAL_TYPE, 'shouldDisabled')
    @Form(FormItemName.CHANGE_CAMERA_PRESET, 'shouldDisabled')
    @Form(FormItemName.STYLE_3D, 'shouldDisabled')
    @skipWithDefault(true)
    public should3DDisable() {
        return this.feature.shouldDisable3D();
    }

    @Form(FormItemName.SCALE_DEPTH_3D, 'shouldDisabled')
    @skipWithDefault(true)
    public shouldDisableDepth() {
        return this.feature.shouldDisableComplex3DProperties();
    }

    @Form(FormItemName.SHOW_FLAT_TEXT, 'shouldDisabled')
    public shouldDisableShowFlatText() {
        return this.feature.shouldDisableShowFlatText();
    }

    @Form(FormItemName.RESET_3D, 'tooltip')
    @Form(FormItemName.LAYOUT_ROTATE_3D_X, 'tooltip')
    @Form(FormItemName.LAYOUT_ROTATE_3D_Y, 'tooltip')
    @Form(FormItemName.LAYOUT_ROTATE_3D_Z, 'tooltip')
    @Form(FormItemName.SCALE_DEPTH_3D, 'tooltip')
    @Form(FormItemName.CHANGE_DEPTH_COLOR_3D, 'tooltip')
    @Form(FormItemName.SHOW_FLAT_TEXT, 'tooltip')
    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_HEIGHT, 'tooltip')
    @Form(FormItemName.SCALE_FRONT_FRUSTUM_3D_WIDTH, 'tooltip')
    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_HEIGHT, 'tooltip')
    @Form(FormItemName.SCALE_BACK_FRUSTUM_3D_WIDTH, 'tooltip')
    @Form(FormItemName.CHANGE_3D_LIGHT_TYPE, 'tooltip')
    @Form(FormItemName.CHANGE_LIGHT_ROTATION_ANGLE, 'tooltip')
    @Form(FormItemName.CHANGE_MATERIAL_TYPE, 'tooltip')
    @Form(FormItemName.CHANGE_CAMERA_PRESET, 'tooltip')
    @UseBefore(ThreeDDisabledTooltipMiddleware)
    public showWebGLNotEnabledTooltip() {
        return '';
    }

    @Form(FormItemName.CHANGE_3D_LIGHT_TYPE, 'options')
    @skipWithDefault({ selectOptions: [] as ISelectOption[] })
    public lightTypeOptions() {
        return {
            selectOptions: this.feature.lightTypeOptions,
        };
    }

    @Form(FormItemName.CHANGE_CAMERA_PRESET, 'options')
    @skipWithDefault({ selectOptions: [] as ISelectOption[] })
    public cameraTypeOptions() {
        return {
            selectOptions: this.feature.cameraTypeOptions,
        };
    }

    @Form(FormItemName.CHANGE_MATERIAL_TYPE, 'options')
    @skipWithDefault({ selectOptions: [] as ISelectOption[] })
    public materialTypeOptions() {
        return {
            selectOptions: this.feature.materialTypeOptions,
        };
    }

    @Form(FormItemName.CHANGE_DEPTH_COLOR_3D, 'content')
    public getColorPicker() {
        return this.feature.getColorSelectButton();
    }
}
